import React, { useState, useEffect } from "react";
import { blogs } from "../../data/mock";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../../components/ui/button";
import {
    Card,
    CardContent,
} from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
    Plus,
    Edit,
    Trash2,
    Search,
} from "lucide-react";
import { useToast } from "../../hooks/use-toast";
import DeleteModal from "../../components/admin/DeleteModal";
import {
    useNavigate,
} from "react-router-dom";
import SearchBar from "../ui/search-bar";
import { fetchBlogs, removeBlog } from "@/hooks/useBlog";

const BlogsManagement = () => {
    const [searchTerm, setSearchTerm] = useState("");
    const [deleteModal, setDeleteModal] = useState({ isOpen: false, blog: null });
    const [blogList, setBlogList] = useState([]);
    const { toast } = useToast();
    const navigate = useNavigate();

    const getAllBlogs = async () => {
        try {
            const res = await fetchBlogs(); // call hook function
            // console.log(res)
            setBlogList(res.blogs || res);
        } catch (err) {
            toast({
                title: "Error",
                description: err.response?.data?.message || "Failed to load blogs.",
                variant: "destructive",
            });
        }
    };

    useEffect(() => {
        getAllBlogs();
    }, []);

    const filteredBlogs = blogList.filter(
        (blog) =>
            blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            blog.tags.some((tag) =>
                tag.toLowerCase().includes(searchTerm.toLowerCase())
            )
    );

    const handleDelete = async (id) => {
        try {
            await removeBlog(id);
            toast({ title: "Deleted", description: "Blog removed successfully." });
            setDeleteModal({ isOpen: false, blog: null });
            getAllBlogs();
            
        } catch (err) {
            toast({
                title: "Error",
                description: err.response?.data?.message || "Failed to delete blog.",
                variant: "destructive",
            });
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
        >
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-3xl font-bold">Blog Management</h2>
                    <p className="text-muted-foreground">
                        Manage your blog posts and Medium integration
                    </p>
                </div>
                <Button
                    className="bg-purple-600 hover:bg-purple-700"
                    onClick={() => navigate("/admin/blogs/add")}
                >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Blog Post
                </Button>
            </div>

            {/* Search */}
            <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />

            {/* Blogs List */}
            <div className="space-y-4">
                <AnimatePresence>
                    {filteredBlogs.map((blog, index) => (
                        <motion.div
                            key={blog._id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                            transition={{ delay: index * 0.1, duration: 0.3 }}
                            whileHover={{ scale: 1.01 }}
                        >
                            <Card className="hover:shadow-lg transition-all duration-300"
                            >
                                <CardContent className="p-6">
                                    <div className="flex items-center gap-4">
                                        <img
                                            src={blog.images[0].url}
                                            alt={blog.title}
                                            className="w-20 h-20 rounded-lg object-cover"
                                        />
                                        <div className="flex-1">
                                            <div className="flex items-start justify-between">
                                                <div className="space-y-2">
                                                    <h3 className="text-lg font-semibold hover:text-purple-600 transition-colors">
                                                        {blog.title}
                                                    </h3>
                                                    <p className="text-sm text-muted-foreground line-clamp-2">
                                                        {blog.excerpt}
                                                    </p>
                                                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                                        <span>
                                                            {new Date(blog.createdAt).toLocaleDateString()}
                                                        </span>
                                                        <span>{blog.readTime}</span>
                                                        {blog.featured && (
                                                            <Badge variant="secondary" className="text-xs">
                                                                Featured
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    <div className="flex gap-2">
                                                        {blog.tags.slice(0, 3).map((tag) => (
                                                            <Badge
                                                                key={tag}
                                                                variant="outline"
                                                                className="text-xs"
                                                            >
                                                                {tag}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                </div>

                                                <div className="flex gap-2">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="hover:bg-blue-50 dark:hover:bg-blue-950/20"
                                                        onClick={() =>
                                                            navigate(`/admin/blogs/edit/${blog?._id}`)
                                                        }
                                                    >
                                                        <Edit className="h-4 w-4 text-blue-600" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="hover:bg-red-50 dark:hover:bg-red-950/20"
                                                        onClick={() =>
                                                            setDeleteModal({ isOpen: true, blog })
                                                        }
                                                    >
                                                        <Trash2 className="h-4 w-4 text-red-600" />
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </motion.div>
                    ))}
                </AnimatePresence>
            </div>

            {/* Delete Modal */}
            <DeleteModal
                isOpen={deleteModal.isOpen}
                onClose={() => setDeleteModal({ isOpen: false, blog: null })}
                onConfirm={() => handleDelete(deleteModal.blog?._id)}
                title="Delete Blog Post"
                description="This will permanently remove the blog post from your portfolio."
                itemName={deleteModal.blog?.title}
            />
        </motion.div>
    );
};

export default BlogsManagement;